import { useEffect, useState } from 'react';
import { Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
    analyticsApi,
    buildTableQuery,
    type AnalyticsEvent,
    type AnalyticsFilters,
    type AnalyticsTable,
} from '../../services/analytics';
import { EventSelect } from './EventSelect';
import { formatInt } from './format';
import { InfoTip } from './InfoTip';
import { METRIC_INFO } from './metricInfo';
import styles from './FeatureUsageByCustomer.module.css';

const ROW_HEIGHT = 36;
const AXIS_HEIGHT = 28;

interface TickProps {
    x?: number | string;
    y?: number | string;
    payload?: { value: string };
    /** Nome da empresa por ID da conta. */
    names: Map<string, string>;
}

/**
 * Nome da empresa alinhado a direita. O eixo usa o ID da conta (empresas com o mesmo nome
 * viram barras separadas), mas so o nome aparece: o ID nao diz nada a quem le o painel.
 */
function CustomerTick({ x = 0, y = 0, payload, names }: TickProps) {
    return (
        <g transform={`translate(${x},${y})`}>
            <text x={-8} y={4} textAnchor="end" fontSize={11} fill="var(--color-gray-800)">
                {names.get(payload?.value ?? '') ?? payload?.value}
            </text>
        </g>
    );
}

interface FeatureUsageByCustomerProps {
    events: AnalyticsEvent[];
    event: string;
    onEventChange: (id: string) => void;
    filters: AnalyticsFilters;
    enabled: boolean;
    refreshKey: number;
}

interface Settled {
    key: string;
    table: AnalyticsTable | null;
    error: string | null;
}

export function FeatureUsageByCustomer({
    events,
    event,
    onEventChange,
    filters,
    enabled,
    refreshKey,
}: FeatureUsageByCustomerProps) {
    const [retry, setRetry] = useState(0);
    const [settled, setSettled] = useState<Settled | null>(null);

    const requestKey = enabled ? `${buildTableQuery(filters, { event })}#${refreshKey}.${retry}` : null;

    useEffect(() => {
        if (!requestKey) return;
        const controller = new AbortController();
        analyticsApi
            .table('uso-por-cliente', filters, { event }, controller.signal)
            .then((table) => setSettled({ key: requestKey, table, error: null }))
            .catch((e: unknown) => {
                if (controller.signal.aborted) return;
                setSettled({
                    key: requestKey,
                    table: null,
                    error: e instanceof Error ? e.message : 'Não foi possível carregar o uso por cliente.',
                });
            });
        return () => controller.abort();
        // requestKey resume filtros, evento e contadores de nova tentativa.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestKey]);

    const loading = requestKey !== null && settled?.key !== requestKey;
    const current = settled && settled.key === requestKey ? settled : null;
    // Mantem o ultimo grafico na tela enquanto o novo carrega, para nao piscar.
    const table = current ? current.table : (settled?.table ?? null);
    const rows = (table?.rows ?? []).map((r) => ({
        id: String(r.contaId ?? r.conta),
        conta: String(r.conta ?? '-'),
        usos: typeof r.usos === 'number' ? r.usos : 0,
    }));

    const names = new Map(rows.map((r) => [r.id, r.conta]));

    return (
        <section className={`${styles.card} ${loading ? styles.loading : ''}`} aria-busy={loading}>
            <header className={styles.header}>
                <div>
                    <h4 className={styles.title}>
                        Uso da funcionalidade por cliente
                        <InfoTip info={METRIC_INFO.usoPorCliente} label="Uso da funcionalidade por cliente" align="start" />
                    </h4>
                    <p className={styles.subtitle}>
                        {formatInt(rows.length)} {rows.length === 1 ? 'conta com uso' : 'contas com uso'}
                    </p>
                </div>
                <EventSelect label="Evento neste visual" value={event} options={events} onChange={onEventChange} />
            </header>

            {current?.error && (
                <div className={styles.error} role="alert">
                    <span>{current.error}</span>
                    <button type="button" className={styles.retry} onClick={() => setRetry((n) => n + 1)}>
                        Tentar novamente
                    </button>
                </div>
            )}

            {!current?.error && table && rows.length === 0 && (
                <p className={styles.empty}>Nenhuma empresa usou esta funcionalidade no período.</p>
            )}

            {rows.length > 0 && (
                <div className={styles.scroll}>
                    <div
                        role="img"
                        aria-label="Uso da funcionalidade por cliente"
                        style={{ height: rows.length * ROW_HEIGHT + AXIS_HEIGHT }}
                    >
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 32, bottom: 0, left: 0 }} barCategoryGap={8}>
                                <CartesianGrid horizontal={false} stroke="var(--color-gray-200)" />
                                <XAxis
                                    type="number"
                                    allowDecimals={false}
                                    tickLine={false}
                                    axisLine={false}
                                    tick={{ fontSize: 11, fill: 'var(--color-gray-500)' }}
                                />
                                <YAxis
                                    type="category"
                                    dataKey="id"
                                    width={150}
                                    interval={0}
                                    tickLine={false}
                                    tick={(props) => <CustomerTick {...props} names={names} />}
                                />
                                <Tooltip
                                    cursor={{ fill: 'var(--color-gray-50)' }}
                                    formatter={(v) => [formatInt(typeof v === 'number' ? v : null), 'Usos']}
                                    labelFormatter={(_, payload) => String(payload?.[0]?.payload?.conta ?? '')}
                                    contentStyle={{
                                        background: 'var(--color-white)',
                                        border: '1px solid var(--color-border-light)',
                                        borderRadius: 8,
                                        fontSize: 12,
                                    }}
                                />
                                <Bar dataKey="usos" fill="var(--color-primary-600)" radius={[0, 4, 4, 0]} isAnimationActive={false}>
                                    <LabelList dataKey="usos" position="right" fontSize={11} fill="var(--color-gray-700)" />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}
        </section>
    );
}
